import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MessageCircle, ArrowUp, Globe } from 'lucide-react';
import { FaFacebookSquare, FaInstagram, FaYoutube } from 'react-icons/fa';
import { DataService } from '../services/dataService';
import { useLanguage } from '../contexts/LanguageContext';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [config, setConfig] = useState(DataService.getConfig());
  const [showScrollTop, setShowScrollTop] = useState(false);
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    const handler = (e: any) => {
      try {
        const db = e?.detail?.db || DataService.getDB();
        if (db && db.config) {
          setConfig(db.config);
        }
      } catch (err) {
        console.warn('Layout db_updated handler error', err);
      }
    };

    window.addEventListener('db_updated', handler as EventListener);

    // Initial sync
    try {
      const db = DataService.getDB();
      if (db && db.config) {
        setConfig(db.config);
      }
    } catch (err) {
      console.warn('Layout initial DB load error', err);
    }

    return () => window.removeEventListener('db_updated', handler as EventListener);
  }, []);

  useEffect(() => {
    // Scroll to top on route change
    window.scrollTo(0, 0);
  }, [location]);


  //const [isMenuOpen, setIsMenuOpen] = useState(false);
  //const [config, setConfig] = useState(DataService.getConfig());
  //const [showScrollTop, setShowScrollTop] = useState(false);
  //const location = useLocation();
  //const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    setConfig(DataService.getConfig());
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: t('Home', 'പൂമുഖം'), path: '/' },
    { name: t('About', 'ക്ഷേത്രത്തെക്കുറിച്ച്'), path: '/about' },
    { name: t('Poojas', 'വഴിപാടുകൾ'), path: '/poojas' },
    { name: t('Gallery', 'ചിത്രശാല'), path: '/gallery' },
    { name: t('Contact', 'ബന്ധപ്പെടുക'), path: '/contact' },
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'ml' : 'en');
  };

  return (
    <div className="flex flex-col min-h-screen font-sans bg-temple-cream text-gray-800">
      {/* Top Bar - Lighter Orange */}
      <div className="bg-temple-orange-100 text-temple-orange-900 py-2 px-4 text-sm hidden md:flex justify-between items-center border-b border-temple-orange-200">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1 font-medium"><Phone size={14} /> {config.phone}</span>
          <span className="text-temple-orange-300">|</span>
          <span className="font-medium">  {language === 'en' ? 'Devi Saranam ..' : 'ദേവി ശരണം  ..'}</span>
        </div>
        <div className="flex items-center gap-4">
          <button 
            onClick={toggleLanguage}
            className="flex items-center gap-1 hover:text-temple-orange-600 transition-colors font-bold text-temple-orange-800"
          >
            <Globe size={14} /> {language === 'en' ? 'മലയാളം' : 'English'}
          </button>
        </div>
      </div>

      {/* Main Navigation */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md shadow-sm border-b-2 border-temple-orange-200">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3 group">
            {/* OM Logo */}
            <div className="w-12 h-12 flex items-center justify-center text-temple-orange-600 font-serif font-bold text-4xl leading-none drop-shadow-sm group-hover:scale-110 transition-transform">
              ॐ
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-serif font-bold text-temple-orange-800 leading-none">
                {language === 'en' ? 'Arunoottimangalam' : 'അറുനൂറ്റിമംഗലം'}
              </h1>
              <p className="text-xs text-temple-orange-600 font-bold tracking-wide uppercase mt-1">
                {language === 'en' ? 'Bhagavathy Temple' : 'ഭഗവതി ക്ഷേത്രം'}
              </p>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex gap-8 items-center">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-medium text-lg transition-colors hover:text-temple-orange-600 ${
                  location.pathname === link.path ? 'text-temple-orange-700 font-bold border-b-2 border-temple-orange-500' : 'text-gray-600'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link to="/donate" className="bg-temple-orange-500 text-white px-5 py-2 rounded-full font-bold hover:bg-temple-orange-600 transition-colors shadow hover:shadow-orange-500/30">
              {t('Donate', 'സംഭാവന')}
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-4">
             <button 
                onClick={toggleLanguage}
                className="text-temple-orange-900 font-bold text-sm bg-orange-100 px-2 py-1 rounded"
              >
                {language === 'en' ? 'MAL' : 'ENG'}
              </button>
            <button
              className="text-temple-orange-900 p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Nav Dropdown */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 absolute w-full shadow-xl">
            <nav className="flex flex-col p-4 gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`text-lg font-medium p-2 rounded-md ${
                    location.pathname === link.path ? 'bg-temple-orange-50 text-temple-orange-800' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/donate"
                onClick={() => setIsMenuOpen(false)}
                className="text-lg font-medium p-2 text-temple-orange-700 bg-orange-50 hover:bg-orange-100 rounded-md"
              >
                {t('Donate Now', 'സംഭാവന നൽകുക')}
              </Link>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Floating Buttons */}
      <div className="fixed bottom-6 right-6 flex flex-col gap-4 z-40">
        {showScrollTop && (
          <button
            onClick={scrollToTop}
            className="bg-white text-temple-orange-600 border border-temple-orange-200 p-3 rounded-full shadow-lg hover:bg-orange-50 transition-all animate-fade-in"
            aria-label="Scroll to top"
          >
            <ArrowUp size={24} />
          </button>
        )}
        <a
          href={`https://wa.me/${config.whatsappNumber}`}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-[#25D366] text-white p-3 rounded-full shadow-lg hover:bg-[#128C7E] transition-all hover:scale-110"
          aria-label="Contact on WhatsApp"
        >
          <MessageCircle size={28} />
        </a>
      </div>

      {/* Footer - Lighter Background */}
      <footer className="bg-temple-orange-100 text-gray-800 pt-12 pb-6 border-t-4 border-temple-orange-200">
        <div className="container mx-auto px-4 grid md:grid-cols-3 gap-8 mb-8">
          <div>
            <h3 className="text-xl font-serif font-bold mb-4 text-temple-orange-900 flex items-center gap-2">
               <span className="text-3xl">ॐ</span>
               {language === 'en' ? 'Arunoottimangalam Bhagavathy' : 'അറുനൂറ്റിമംഗലം ഭഗവതി'}
            </h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              {t(
                'A sacred place of worship dedicated to Goddess Bhagavathy. May the divine mother bless you with peace, prosperity, and happiness.',
                'ഭഗവതിക്ക് സമർപ്പിച്ചിരിക്കുന്ന പുണ്യ ആരാധനാലയം. അമ്മയുടെ അനുഗ്രഹം നിങ്ങൾക്ക് സമാധാനവും ഐശ്വര്യവും സന്തോഷവും നൽകട്ടെ.'
              )}
            </p>
          </div>
          <div>
            <h3 className="text-lg font-bold mb-4 text-temple-orange-800">{t('Quick Links', 'പെട്ടെന്നുള്ള ലിങ്കുകൾ')}</h3>
            <ul className="space-y-2">
              <li><Link to="/poojas" className="text-blue-600 hover:text-blue-800 transition-colors">{t('Poojas & Offerings', 'വഴിപാടുകൾ')}</Link></li>
              <li><Link to="/gallery" className="text-blue-600 hover:text-blue-800 transition-colors">{t('Photo Gallery', 'ചിത്രശാല')}</Link></li>
              <li><Link to="/contact" className="text-blue-600 hover:text-blue-800 transition-colors">{t('Location & Contact', 'ബന്ധപ്പെടുക')}</Link></li>
              <li><Link to="/donate" className="text-blue-600 hover:text-blue-800 transition-colors">{t('Donate', 'സംഭാവന')}</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-bold mb-4 text-temple-orange-800">{t('Contact Info', 'വിലാസം')}</h3>
            <ul className="space-y-3 text-gray-700">
              <li className="flex gap-2 items-start">
                <span className="font-semibold text-temple-orange-700">Address:</span> {language === 'en' ? config.address : (config.addressMal || config.address)}
              </li>
              <li className="flex gap-2 items-center">
                <span className="font-semibold text-temple-orange-700">Phone:</span> {config.phone}
              </li>
              <li className="flex gap-2 items-center">
                <span className="font-semibold text-temple-orange-700">Email:</span> {config.email}
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-temple-orange-200 pt-6 text-center text-sm text-gray-600">
          {(config.facebookUrl || config.youtubeUrl) && (
            <div className="flex justify-center gap-6 mb-4 text-temple-orange-700">
              {config.facebookUrl && (
                <a href={config.facebookUrl} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:text-temple-orange-900">
                  <FaFacebookSquare size={24} />
                </a>
              )}
              {config.youtubeUrl && (
                <a href={config.youtubeUrl} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="hover:text-temple-orange-900">
                  <FaYoutube size={24} />
                </a>
              )}
            </div>
          )}
          <p>&copy; {new Date().getFullYear()} {config.templeName}. All rights reserved.</p>
          <p className="mt-2">
            <a href="https://www.digegain.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">designed by @digegain</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
